/**
 * Vietnamese display formatting.
 *
 * The Staff App is Vietnamese-only by product decision: the multi-language
 * requirement in the spec applies to the customer QR ordering flow, not to the
 * employee-facing apps. There is therefore no i18n layer here on purpose —
 * strings are literals, which keeps them greppable and reviewable by the
 * restaurant's own managers.
 *
 * MONEY: every amount arriving from the API is an integer in MINOR currency
 * units. `formatMoney` from @sonauto/utilities is the only thing allowed to
 * turn one into a string, so a payslip and a phone round identically.
 */
import { formatMoney, money } from '@sonauto/utilities';

/** Minor units → "25.000 ₫". VND has exponent 0; other currencies pass theirs. */
export function formatMinor(amountMinor: number, currency = 'VND', exponent = 0): string {
  return formatMoney(money(Math.trunc(amountMinor), currency, exponent), 'vi-VN');
}

/** KPI points are stored in milli-points. 1250 → "1,25". */
export function formatPointsMilli(pointsMilli: number): string {
  return (pointsMilli / 1000).toLocaleString('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Signed variant for the per-rule KPI breakdown: "+1,25" / "−0,50". */
export function formatSignedPointsMilli(pointsMilli: number): string {
  if (pointsMilli === 0) return '0';
  const sign = pointsMilli > 0 ? '+' : '−';
  return `${sign}${formatPointsMilli(Math.abs(pointsMilli))}`;
}

/** Basis points → "12,5%". */
export function formatBp(bp: number): string {
  return `${(bp / 100).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;
}

/** Minutes → "7 giờ 32 phút" / "45 phút". */
export function formatMinutes(totalMinutes: number): string {
  const safe = Math.max(0, Math.trunc(totalMinutes));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  if (hours === 0) return `${minutes} phút`;
  if (minutes === 0) return `${hours} giờ`;
  return `${hours} giờ ${minutes} phút`;
}

/** Seconds → "07:32:10" for the live-ticking worked-time display. */
export function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.trunc(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/** Seconds → "1:45" for offer countdowns, which are always under an hour. */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.trunc(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Epoch ms → "14:35". */
export function formatTime(at: number | null | undefined): string {
  if (at === null || at === undefined) return '—';
  return new Date(at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Epoch ms → "14:35 · 25/07". */
export function formatTimeDate(at: number | null | undefined): string {
  if (at === null || at === undefined) return '—';
  const d = new Date(at);
  return `${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false })} · ${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}`;
}

/** "2026-07-25" → "Thứ Sáu, 25/07". */
export function formatBusinessDate(businessDate: string): string {
  const parsed = Date.parse(`${businessDate}T00:00:00Z`);
  if (Number.isNaN(parsed)) return businessDate;
  const d = new Date(parsed);
  return `${WEEKDAYS[d.getUTCDay()] ?? ''}, ${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const WEEKDAYS: readonly string[] = [
  'Chủ Nhật',
  'Thứ Hai',
  'Thứ Ba',
  'Thứ Tư',
  'Thứ Năm',
  'Thứ Sáu',
  'Thứ Bảy',
];

export function weekdayShort(businessDate: string): string {
  const parsed = Date.parse(`${businessDate}T00:00:00Z`);
  if (Number.isNaN(parsed)) return '';
  const short = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
  return short[new Date(parsed).getUTCDay()] ?? '';
}

/** Distance in metres, rounded the way a human reads it. */
export function formatDistance(metres: number | null | undefined): string {
  if (metres === null || metres === undefined) return 'không xác định';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} km`;
}

/** Clock skew in ms → a sentence an employee can act on. */
export function formatSkew(skewMs: number | null | undefined): string {
  if (skewMs === null || skewMs === undefined) return 'không gửi giờ thiết bị';
  const seconds = Math.round(Math.abs(skewMs) / 1000);
  if (seconds < 60) return `lệch ${seconds} giây`;
  const minutes = Math.round(seconds / 60);
  return `lệch ${minutes} phút so với giờ máy chủ`;
}

/** "YYYY-MM-DD" for a Date, in the device's own local calendar. */
export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
